import React, { useMemo } from 'react';
import {
  type AcademyOperatingProfileInput,
  type AcademySizeBand,
  type LocationCountBand,
  type OnboardingWorkflow,
  type OperatingRole,
  type RacquetSportId,
  type SubscriptionTier,
  RACQUET_SPORT_IDS,
  RACQUET_SPORT_LABELS,
  workflowOptionsForOperatingProfile,
} from '@court-sync/shared';
import { Input, Field, FieldLabel } from '@/components/ui';
import { SportIconTile } from '@/components/sport/SportIcons';
import { OnboardingPlanSelection } from './OnboardingPlanSelection';
import { SelectableCard, SelectableCardGrid } from './SelectableCard';
import {
  ACADEMY_SIZE_LABELS,
  LOCATION_COUNT_LABELS,
  ONBOARDING_WORKFLOW_LABELS,
  OPERATING_ROLE_LABELS,
  buildPlanRecommendation,
  deriveOperatingProfileFlags,
} from '../../utils/planRecommendationUi';

export const QUESTIONNAIRE_STEP_IDS = [
  'sports',
  'role',
  'size',
  'coaches',
  'locations',
  'priorities',
  'plan',
] as const;

export type QuestionnaireStepId = (typeof QUESTIONNAIRE_STEP_IDS)[number];

export const QUESTIONNAIRE_WIZARD_STEPS = [
  {
    id: 'sports',
    title: 'Which racquet sports do you offer?',
    description: 'Select all that apply. Choose one as your primary sport.',
  },
  {
    id: 'role',
    title: 'What best describes your role?',
    description: 'This helps us tailor setup suggestions — nothing is locked in.',
  },
  {
    id: 'size',
    title: 'How many students do you manage?',
    description: 'An approximate range is fine.',
  },
  {
    id: 'coaches',
    title: 'How do you want to manage coaches?',
    description: 'Choose how coaches appear in CourtSync — you can change this later.',
  },
  {
    id: 'locations',
    title: 'How many locations do you operate?',
    description: 'Courts or venues where you run programs.',
  },
  {
    id: 'priorities',
    title: 'What matters to you?',
    description:
      'Select what you run or plan to offer — we tailor your setup checklist and plan recommendation from this.',
  },
  {
    id: 'plan',
    title: 'Choose your plan',
    description: 'Based on your answers — compare pricing and pick what fits.',
  },
] as const;

export type QuestionnaireWizardStep = (typeof QUESTIONNAIRE_WIZARD_STEPS)[number];

export function shouldShowCoachesStep(profile: Partial<AcademyOperatingProfileInput>): boolean {
  return profile.operatingRole !== 'solo_coach';
}

export function resolveQuestionnaireWizardSteps(
  profile: Partial<AcademyOperatingProfileInput>
): QuestionnaireWizardStep[] {
  return QUESTIONNAIRE_WIZARD_STEPS.filter(
    (step) => step.id !== 'coaches' || shouldShowCoachesStep(profile)
  );
}

export function applyOperatingRoleSelection(
  profile: Partial<AcademyOperatingProfileInput>,
  role: OperatingRole
): Partial<AcademyOperatingProfileInput> {
  if (role === 'solo_coach') {
    const desiredWorkflows = (profile.desiredWorkflows ?? []).filter(
      (workflow): workflow is OnboardingWorkflow => workflow !== 'coach_portals'
    );
    return {
      ...profile,
      operatingRole: role,
      coachCountBand: '0',
      coachesNeedPortalAccess: false,
      desiredWorkflows,
    };
  }

  const next: Partial<AcademyOperatingProfileInput> = { ...profile, operatingRole: role };
  if (profile.operatingRole === 'solo_coach') {
    next.coachCountBand = undefined;
    next.coachesNeedPortalAccess = undefined;
  }
  return next;
}

export function isPrioritiesStepComplete(
  profile: Partial<AcademyOperatingProfileInput>
): boolean {
  return (profile.desiredWorkflows?.length ?? 0) > 0;
}

function setCoachPortalAccess(
  profile: Partial<AcademyOperatingProfileInput>,
  needsPortalAccess: boolean
): Partial<AcademyOperatingProfileInput> {
  const workflows = profile.desiredWorkflows ?? [];
  const desiredWorkflows: OnboardingWorkflow[] = needsPortalAccess
    ? workflows.includes('coach_portals')
      ? workflows
      : [...workflows, 'coach_portals']
    : workflows.filter((workflow): workflow is OnboardingWorkflow => workflow !== 'coach_portals');

  return {
    ...profile,
    coachesNeedPortalAccess: needsPortalAccess,
    desiredWorkflows,
  };
}

function workflowOptionsForProfile(
  profile: Partial<AcademyOperatingProfileInput>
): OnboardingWorkflow[] {
  return workflowOptionsForOperatingProfile(profile);
}

export interface AcademyNeedsQuestionnaireProps {
  stepId: QuestionnaireStepId;
  profile: Partial<AcademyOperatingProfileInput>;
  onProfileChange: (profile: Partial<AcademyOperatingProfileInput>) => void;
  selectedPlanTier?: SubscriptionTier;
  onSelectPlanTier?: (tier: SubscriptionTier, options?: { starterAcknowledged?: boolean }) => void;
  marketCountry?: string | null;
}

export function isQuestionnaireStepComplete(
  stepId: QuestionnaireStepId,
  profile: Partial<AcademyOperatingProfileInput>
): boolean {
  switch (stepId) {
    case 'sports': {
      const sports = profile.sports ?? [];
      if (sports.length === 0) return false;
      const primaryCount = sports.filter((s) => s.isPrimary).length;
      if (primaryCount !== 1) return false;
      const other = sports.find((s) => s.sportId === 'other');
      if (other && !other.customLabel?.trim()) return false;
      return true;
    }
    case 'role':
      return Boolean(profile.operatingRole);
    case 'size':
      return Boolean(profile.sizeBand);
    case 'coaches':
      if (!shouldShowCoachesStep(profile)) return true;
      return typeof profile.coachesNeedPortalAccess === 'boolean';
    case 'locations':
      return Boolean(profile.locationCountBand);
    case 'priorities':
      return isPrioritiesStepComplete(profile);
    case 'plan':
      return true;
    default:
      return false;
  }
}

function toggleSport(
  profile: Partial<AcademyOperatingProfileInput>,
  sportId: RacquetSportId
): Partial<AcademyOperatingProfileInput> {
  const sports = [...(profile.sports ?? [])];
  const existingIndex = sports.findIndex((s) => s.sportId === sportId);
  if (existingIndex >= 0) {
    sports.splice(existingIndex, 1);
    if (sports.length > 0 && !sports.some((s) => s.isPrimary)) {
      sports[0] = { ...sports[0], isPrimary: true };
    }
    return { ...profile, sports };
  }
  const isFirst = sports.length === 0;
  sports.push({ sportId, isPrimary: isFirst });
  return { ...profile, sports };
}

function setPrimarySport(
  profile: Partial<AcademyOperatingProfileInput>,
  sportId: RacquetSportId
): Partial<AcademyOperatingProfileInput> {
  const sports = (profile.sports ?? []).map((s) => ({
    ...s,
    isPrimary: s.sportId === sportId,
  }));
  return { ...profile, sports };
}

function toggleMultiValue<T extends string>(
  values: T[] | undefined,
  value: T
): T[] {
  const current = values ?? [];
  return current.includes(value)
    ? current.filter((v) => v !== value)
    : [...current, value];
}

export function AcademyNeedsQuestionnaire({
  stepId,
  profile,
  onProfileChange,
  selectedPlanTier,
  onSelectPlanTier,
  marketCountry,
}: AcademyNeedsQuestionnaireProps) {
  const derivedProfile = useMemo(() => deriveOperatingProfileFlags(profile), [profile]);
  const recommendation = useMemo(
    () => buildPlanRecommendation(derivedProfile),
    [derivedProfile]
  );

  switch (stepId) {
    case 'sports':
      return (
        <SportsStep profile={profile} onProfileChange={onProfileChange} />
      );
    case 'role':
      return (
        <SelectableCardGrid label="Operating role">
          {(Object.keys(OPERATING_ROLE_LABELS) as OperatingRole[]).map((role) => (
            <SelectableCard
              key={role}
              id={`role-${role}`}
              title={OPERATING_ROLE_LABELS[role]}
              selected={profile.operatingRole === role}
              onSelect={() => onProfileChange(applyOperatingRoleSelection(profile, role))}
            />
          ))}
        </SelectableCardGrid>
      );
    case 'size':
      return (
        <SelectableCardGrid label="Academy size">
          {(Object.keys(ACADEMY_SIZE_LABELS) as AcademySizeBand[]).map((band) => (
            <SelectableCard
              key={band}
              id={`size-${band}`}
              title={ACADEMY_SIZE_LABELS[band]}
              selected={profile.sizeBand === band}
              onSelect={() => onProfileChange({ ...profile, sizeBand: band })}
            />
          ))}
        </SelectableCardGrid>
      );
    case 'coaches':
      return (
        <SelectableCardGrid label="Coach management">
          <SelectableCard
            id="coaches-basic"
            title="Basic coach info"
            description="Add names and contact details for scheduling. You manage lessons and student records from your dashboard."
            selected={profile.coachesNeedPortalAccess === false}
            onSelect={() => onProfileChange(setCoachPortalAccess(profile, false))}
          />
          <SelectableCard
            id="coaches-portal"
            title="Coach logins"
            description="Each coach gets their own account to view their schedule, mark attendance, and track student progress."
            selected={profile.coachesNeedPortalAccess === true}
            onSelect={() => onProfileChange(setCoachPortalAccess(profile, true))}
          />
        </SelectableCardGrid>
      );
    case 'locations':
      return (
        <SelectableCardGrid label="Location count">
          {(Object.keys(LOCATION_COUNT_LABELS) as LocationCountBand[]).map((band) => (
            <SelectableCard
              key={band}
              id={`locations-${band}`}
              title={LOCATION_COUNT_LABELS[band]}
              selected={profile.locationCountBand === band}
              onSelect={() => onProfileChange({ ...profile, locationCountBand: band })}
            />
          ))}
        </SelectableCardGrid>
      );
    case 'priorities':
      return (
        <PrioritiesStep profile={profile} onProfileChange={onProfileChange} />
      );
    case 'plan':
      return (
        <OnboardingPlanSelection
          recommendation={recommendation}
          selectedPlanTier={selectedPlanTier ?? recommendation.recommendedTier}
          onSelectPlanTier={onSelectPlanTier}
          marketCountry={marketCountry}
        />
      );
    default:
      return null;
  }
}

function PrioritiesStep({
  profile,
  onProfileChange,
}: {
  profile: Partial<AcademyOperatingProfileInput>;
  onProfileChange: (profile: Partial<AcademyOperatingProfileInput>) => void;
}) {
  return (
    <SelectableCardGrid label="Programs and operations">
      {workflowOptionsForProfile(profile).map((workflow) => {
        const selected = profile.desiredWorkflows?.includes(workflow) ?? false;
        return (
          <SelectableCard
            key={workflow}
            id={`workflow-${workflow}`}
            title={ONBOARDING_WORKFLOW_LABELS[workflow]}
            selected={selected}
            onSelect={() =>
              onProfileChange({
                ...profile,
                desiredWorkflows: toggleMultiValue(profile.desiredWorkflows, workflow),
              })
            }
          />
        );
      })}
    </SelectableCardGrid>
  );
}

function SportsStep({
  profile,
  onProfileChange,
}: {
  profile: Partial<AcademyOperatingProfileInput>;
  onProfileChange: (profile: Partial<AcademyOperatingProfileInput>) => void;
}) {
  const selectedSports = profile.sports ?? [];
  const otherSport = selectedSports.find((s) => s.sportId === 'other');

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2" role="group" aria-label="Sports offered">
        {RACQUET_SPORT_IDS.map((sportId) => {
          const selected = selectedSports.some((s) => s.sportId === sportId);
          const isPrimary = selectedSports.find((s) => s.sportId === sportId)?.isPrimary;
          return (
            <div key={sportId} className="space-y-2">
              <SelectableCard
                id={`sport-${sportId}`}
                title={RACQUET_SPORT_LABELS[sportId]}
                icon={<SportIconTile sportId={sportId} />}
                iconContainerClassName="contents"
                badge={isPrimary ? 'Primary' : undefined}
                selected={selected}
                onSelect={() => onProfileChange(toggleSport(profile, sportId))}
              />
              {selected && selectedSports.length > 1 ? (
                <button
                  type="button"
                  className="text-xs font-medium text-primary-600 hover:underline dark:text-primary-400"
                  onClick={() => onProfileChange(setPrimarySport(profile, sportId))}
                >
                  {isPrimary ? 'Primary sport' : 'Set as primary'}
                </button>
              ) : null}
            </div>
          );
        })}
      </div>
      {otherSport ? (
        <Field>
          <FieldLabel htmlFor="other-sport-label">Name for other sport</FieldLabel>
          <Input
            id="other-sport-label"
            value={otherSport.customLabel ?? ''}
            onChange={(event) => {
              const sports = (profile.sports ?? []).map((s) =>
                s.sportId === 'other' ? { ...s, customLabel: event.target.value } : s
              );
              onProfileChange({ ...profile, sports });
            }}
            placeholder="e.g. Racquetball"
            maxLength={80}
          />
        </Field>
      ) : null}
    </div>
  );
}
